<template>
  <!-- Loading Screen -->
  <LoadingScreen
    v-if="isInitialLoading"
    title="Loading Dashboard"
    subtitle="Setting up your workspace..."
  />

  <div v-else class="flex flex-col lg:flex-row overflow-hidden h-screen shadow-md w-full relative">

    <!-- Mobile Sidebar (Overlay) -->
    <Drawer v-model:visible="navVisible" :baseZIndex="1000" position="left" class="w-30 lg:hidden p-0">
         <template #header>
            <SidebarSchoolHeader :name="school?.name" :logo="school?.logo" />
        </template>
        <AdminMobileView :hideMenu="hideMenu" />
    </Drawer>

    <!-- Desktop Sidebar (Static) -->
    <aside :class="[
      'hidden lg:flex flex-col overflow-y-hidden duration-300 ease-linear dark:bg-boxdark z-20 bg-white border-r border-gray-200',
      isCollapsed ? 'w-[80px] items-center' : 'w-64'
    ]" class="h-screen transition-all">

      <!-- Desktop Sidebar Header: logo + the school's full name, never truncated -->
      <div class="w-full border-b border-gray-100 shrink-0" :class="isCollapsed ? 'px-2 py-5' : 'px-5 py-5'">
          <SidebarSchoolHeader :name="school?.name" :logo="school?.logo" :collapsed="isCollapsed" />
      </div>

      <!-- Desktop Sidebar Content -->
      <div class="no-scrollbar flex flex-col overflow-y-auto duration-300 ease-linear flex-1 w-full">
        <div class="flex flex-col w-full">
          <div v-if="!isCollapsed"
            class="gap-2 self-stretch px-6 pt-5 pb-2 w-full text-[11px] font-semibold tracking-wider text-gray-400 uppercase">
            Main menu
          </div>
          <ul class="flex flex-col gap-0.5 px-3 w-full" :class="isCollapsed ? 'pt-4' : ''">
            <SidebarMenuItem v-for="(item, i) in filteredMenuItems" :key="i" :isActiveRoute="isActiveRoute(item.path)"
              :isCollapsed="isCollapsed" :routeName="item.routeName" :tooltipText="item.tooltipText"
              :menuText="dynamicMenuText(item)" :menuIcon="item.menuIcon" :school="school || null"
              :badgeCount="item.routeName === 'admin-chat' ? chatStore.totalUnread : undefined" />

            <!-- AI Workspace: per-school flag + ai_assistant.view grant -->
            <SidebarMenuItem v-if="showAdminEntry" :isActiveRoute="isActiveRoute('/admin/ai-assistant')"
              :isCollapsed="isCollapsed" routeName="admin-ai-assistant" tooltipText="AI Workspace"
              menuText="AI Workspace" menuIcon="pi-sparkles" :school="school || null" />
          </ul>

          <div v-if="!isCollapsed"
            class="gap-2.5 self-stretch px-6 pt-6 pb-2 w-full text-[11px] font-semibold tracking-wider text-gray-400 uppercase">
            Administration
          </div>
          <ul class="flex flex-col gap-0.5 px-3 w-full pb-6" :class="isCollapsed ? 'pt-4' : ''">

            <!-- Billing -->
            <router-link :to="{ name: routeNames.ADMIN_BILLING_DASHBOARD.name }"
              v-if="can(userStore.currentUser, AppPermissions.Billing.Actions.ViewBillItems, AppPermissions.Billing.Resource)"
              data-testid="sidebar-menu-item-admin-billing"
              class="flex gap-3 items-center px-4 py-2.5 w-full text-sm font-medium rounded-lg transition-colors duration-200"
              :class="[
                isActiveRoute('/admin/billing') ? 'bg-primary text-white' : 'text-gray-600 hover:bg-primary/10 hover:text-primary'
              ]" v-tooltip.right="isCollapsed ? 'Billing' : null">
              <i class="pi pi-wallet text-lg"></i>
              <span v-if="!isCollapsed">Billing</span>
            </router-link>

            <!-- Certificates -->
            <router-link :to="{ name: routeNames.ADMIN_CERTIFICATES.name }"
              v-if="isTertiary && can(userStore.currentUser, AppPermissions.Certificates.Actions.View, AppPermissions.Certificates.Resource)"
              class="flex gap-3 items-center px-4 py-2.5 w-full text-sm font-medium rounded-lg transition-colors duration-200"
              :class="[
                isActiveRoute('/admin/certificates') ? 'bg-primary text-white' : 'text-gray-600 hover:bg-primary/10 hover:text-primary'
              ]" v-tooltip.right="isCollapsed ? 'Certificates' : null">
              <i class="pi pi-verified text-lg"></i>
              <span v-if="!isCollapsed">Certificates</span>
            </router-link>

            <!-- Forms -->
            <router-link :to="{ name: 'admin-forms' }"
              v-if="can(userStore.currentUser, AppPermissions.Forms.Actions.View, AppPermissions.Forms.Resource)"
              class="flex gap-3 items-center px-4 py-2.5 w-full text-sm font-medium rounded-lg transition-colors duration-200"
              :class="[
                isActiveRoute('/admin/forms') ? 'bg-primary text-white' : 'text-gray-600 hover:bg-primary/10 hover:text-primary'
              ]" v-tooltip.right="isCollapsed ? 'Forms' : null">
              <i class="pi pi-list text-lg"></i>
              <span v-if="!isCollapsed">Forms</span>
            </router-link>

            <!-- Settings -->
            <router-link :to="{ name: 'admin-permissions' }"
              v-if="can(userStore.currentUser, AppPermissions.Permissions.Actions.View, AppPermissions.Permissions.Resource)"
              class="flex gap-3 items-center px-4 py-2.5 w-full text-sm font-medium rounded-lg transition-colors duration-200"
              :class="[
                isActiveRoute('/admin/permissions') ? 'bg-primary text-white' : 'text-gray-600 hover:bg-primary/10 hover:text-primary'
              ]" v-tooltip.right="isCollapsed ? 'Permissions' : null">
              <i class="pi pi-lock text-lg"></i>
              <span v-if="!isCollapsed">Permissions</span>
            </router-link>

            <router-link :to="{ name: 'admin-tickets' }"
              v-if="can(userStore.currentUser, AppPermissions.Tickets.Actions.View, AppPermissions.Tickets.Resource)"
              class="flex gap-3 items-center px-4 py-2.5 w-full text-sm font-medium rounded-lg transition-colors duration-200"
              :class="[
                isActiveRoute('/admin/tickets') ? 'bg-primary text-white' : 'text-gray-600 hover:bg-primary/10 hover:text-primary'
              ]" v-tooltip.right="isCollapsed ? 'Support' : null">
              <i class="pi pi-comments text-lg"></i>
              <span v-if="!isCollapsed">Support</span>
            </router-link>

            <router-link :to="{ name: 'admin-settings' }"
              v-if="can(userStore.currentUser, AppPermissions.SchoolProfile.Actions.View, AppPermissions.SchoolProfile.Resource)"
              class="flex gap-3 items-center px-4 py-2.5 w-full text-sm font-medium rounded-lg transition-colors duration-200"
              :class="[
                isActiveRoute('/admin/settings') ? 'bg-primary text-white' : 'text-gray-600 hover:bg-primary/10 hover:text-primary'
              ]" v-tooltip.right="isCollapsed ? 'Settings' : null">
              <i class="pi pi-cog text-lg"></i>
              <span v-if="!isCollapsed">Settings</span>
            </router-link>

          </ul>

        </div>
      </div>

      <!-- What's new and Logout pinned to the bottom so they stay reachable on long menus -->
      <div class="w-full border-t border-gray-100 px-3 py-3 shrink-0 flex flex-col gap-0.5">
        <WhatsNewMenu :collapsed="isCollapsed" />
        <button @click="showLogoutDialog = true"
          class="flex gap-3 items-center px-4 py-2.5 w-full rounded-lg cursor-pointer transition-colors duration-200 text-red-600 hover:bg-red-50"
          v-tooltip.right="isCollapsed ? 'Log Out' : null">
          <i class="pi pi-sign-out text-lg"></i>
          <span v-if="!isCollapsed" class="font-medium text-sm">Log Out</span>
        </button>
      </div>
    </aside>

    <Dialog v-model:visible="showLogoutDialog" modal header="Confirm Logout" :style="{ width: '90vw', maxWidth: '350px' }">
      <div class="mb-4">Are you sure you want to log out?</div>
      <div class="flex justify-end gap-2">
        <button class="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300" @click="showLogoutDialog = false">Cancel</button>
        <button class="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700" @click="confirmLogout">Logout</button>
      </div>
    </Dialog>

    <!-- Main Content Area -->
    <div class="relative flex flex-1 flex-col overflow-y-auto overflow-x-hidden h-full w-full bg-gray-50">

      <!-- Desktop Header -->
      <AdminNavbar />

      <!-- Mobile Header -->
      <header class="flex items-center justify-between p-4 bg-white border-b border-gray-200 lg:hidden shadow-sm z-30 sticky top-0">
          <div class="flex min-w-0 flex-1 items-center gap-3">
             <Button icon="pi pi-bars" @click="showMenu" class="p-button-text p-button-rounded text-gray-600" aria-label="Menu" />
             <span class="min-w-0 font-bold text-lg text-gray-800 truncate">{{ school?.name }}</span>
          </div>
          <div class="flex shrink-0 items-center gap-2">
             <NotificationWidget />
             <DownloadManager />
             <VideoHelpButton />
             <UserProfileWidget />
          </div>
      </header>

      <div class="flex-1 w-full max-w-[100vw]">
        <AnnouncementBanner />
        <router-view v-slot="{ Component }">
          <RouterViewComponent :is="Component" />
           <ModalViewRouter />
        </router-view>
      </div>

    </div>

    <SchoolSetupWizard />
    <SetupNotificationWidget />
    <PermissionAccessWidget />
    <GlobalSearchDialog />
    <AppUpdateNotification />
    <ConnectivityBanner />
    <!-- Global AI assistant: floating launcher + slide-over chat, on every admin page -->
    <AiAssistantWidget />
    <WhatsNewDrawer />
  </div>
</template>

<script setup lang="ts">
import Tooltip from 'primevue/tooltip';
import AdminMobileView from './AdminMobileView.vue';
import { routeNames } from '@/constants/route-details.constant';
import { useSchoolQuery } from '@/composables/queries/useSchoolQuery';
import { useSchoolFeatures } from '@/composables/useSchoolFeatures';
import { useUserStore } from '@/store/userStore';
import { useNotificationStore } from '@/store/notificationStore';
import NotificationWidget from '@/components/common/NotificationWidget.vue';
import AnnouncementBanner from '@/components/common/AnnouncementBanner.vue';
import DownloadManager from '@/components/common/DownloadManager.vue';
import { useToast } from 'primevue/usetoast';
import { menubarItems, type MenuItem } from '@/constants/route-details.constant';
import SidebarMenuItem from '@/components/common/SidebarMenuItem.vue';
import SidebarSchoolHeader from '@/components/common/SidebarSchoolHeader.vue';
import AiAssistantWidget from '@/components/common/AiAssistantWidget.vue';
import WhatsNewMenu from '@/components/common/WhatsNew/WhatsNewMenu.vue';
import WhatsNewDrawer from '@/components/common/WhatsNew/WhatsNewDrawer.vue';
import { useAiAssistantAccess } from '@/composables/useAiAssistantAccess';
import SchoolSetupWizard from '@/components/DashBoard/Modal/SchoolSetupWizard.vue';
import SetupNotificationWidget from '@/components/common/SetupNotificationWidget.vue';
import AdminNavbar from '@/components/DashBoard/common/AdminNavbar.vue';
import UserProfileWidget from '@/components/DashBoard/common/UserProfileWidget.vue';
import LoadingScreen from '@/components/common/LoadingScreen.vue';
import PermissionAccessWidget from '@/components/common/PermissionAccessWidget.vue';
import { can, AppPermissions } from '@/utils/permissions';
import GlobalSearchDialog from '@/components/DashBoard/common/GlobalSearchDialog.vue';
import AppUpdateNotification from '@/components/common/AppUpdateNotification.vue';
import { provideAppTour } from '@/composables/tour/useAppTour';
import { useChatNotifications } from '@/composables/useChatNotifications';
import { useChatStore } from '@/store/chatStore';

provideAppTour();

// register tooltip globally for this component
defineExpose({
  directives: { tooltip: Tooltip },
});

const toast = useToast();
const route = useRoute();
const { school } = useSchoolQuery();
const { isTertiary } = useSchoolFeatures();
const { showAdminEntry } = useAiAssistantAccess();
const navVisible = ref(false);
const userStore = useUserStore();
const notifStore = useNotificationStore();
const chatStore = useChatStore();
useChatNotifications();
const showLogoutDialog = ref(false);

// Loading state - show loading screen until both user and school data are loaded
const isInitialLoading = computed(() => !school.value || !userStore.currentUser);

// Function to show the menu
const showMenu = () => {
  navVisible.value = true;
};

// Function to hide the menu
const hideMenu = () => {
  navVisible.value = false;
};

const isActiveRoute = (path: string) => route.path === path || route.path.startsWith(path);
const isCollapsed = ref(false);

// Method to toggle sidebar state
const toggleSidebar = () => {
  isCollapsed.value = !isCollapsed.value;
};

const checkViewportSize = () => {
  isCollapsed.value = window.innerWidth < 1400;
};

// filter based on school type
const filteredMenuItems = computed(() => {
  if (!school.value || !userStore.currentUser) return menubarItems;

  const type = school.value.school_type?.toLowerCase();
  const isParent = (school.value as { is_parent?: boolean; parent_features_enabled?: boolean }).is_parent;
  const parentFeaturesEnabled = (school.value as { parent_features_enabled?: boolean }).parent_features_enabled;
  return menubarItems.filter(item => {
    if (item.permissions) {
      if (!item.permissions.some(p => can(userStore.currentUser, p.action, p.resource))) {
        return false;
      }
    } else if (item.permission) {
      if (!can(userStore.currentUser, item.permission.action, item.permission.resource)) {
        return false;
      }
    }

    if (item.showFor === 'parent') return !!isParent || !!parentFeaturesEnabled;
    if (item.showFor === 'all') return true;
    if (item.showFor === 'tertiary' && type === 'tertiary') return true;
    if (item.showFor === 'non-tertiary' && type !== 'tertiary') return true;
    return false;
  });
});

import { useSchoolLabels } from '@/composables/useSchoolLabels';

const { labels } = useSchoolLabels();

// adjust menu text dynamically (e.g., Lecturers vs Teachers)
const dynamicMenuText = (item: MenuItem) => {
  if (item.routeName === 'admin-teacher') {
    return labels.value.TEACHER;
  }
  if (item.routeName === 'admin-student') {
    return labels.value.STUDENT + 's';
  }
  if (item.routeName === 'admin-parent') {
    // Parents usually stay Parents, but if there was a label for it...
    return item.menuText;
  }
  return item.menuText;
};

// Use the logout method from userStore
const handleLogout = async() => {
  try {
    await userStore.logout(); // Call the logout method from userStore
    // Use hard redirect to fully reset all Vue state, Apollo cache, and stores
    window.location.href = '/login';
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Error during logout', life: 3000 });
  }
};

const confirmLogout = async() => {
  showLogoutDialog.value = false;
  await handleLogout();
};

onMounted(() => {
  checkViewportSize();
  window.addEventListener('resize', checkViewportSize);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkViewportSize);
});

// Provide the state and toggle function
provide('isCollapsed', isCollapsed);
provide('toggleSidebar', toggleSidebar);

// Global Notifications
import { useSubscription } from '@vue/apollo-composable';
import { ON_USER_NOTIFICATION } from '@/graphql/subscriptions/tickets';

const currentUser = computed(() => userStore.currentUser);
// Only subscribe if we have a user
const { onResult: onNotification } = useSubscription(ON_USER_NOTIFICATION, () => ({
  id: currentUser.value?.id,
  type: 'user',
}), {
  enabled: computed(() => !!currentUser.value?.id),
});

onNotification((result) => {
  if (result.data?.userNotification) {
    const notif = result.data.userNotification;
    const metadata = (notif.metadata || {}) as Record<string, unknown>;

    // Prefer the persisted row id: it is what the hydrated history uses, so the
    // live copy dedupes against it and can be marked read server-side (#224).
    // Only an unpersisted (group fan-out) notification falls back to a local id.
    const persisted = !!notif.id;

    notifStore.addNotification({
      id: persisted
        ? String(notif.id)
        : `local-${(metadata.submission_id as string) || Date.now().toString()}`,
      title: notif.title,
      message: notif.message,
      type: notif.type || 'system',
      read: !!notif.read_at,
      created_at: notif.created_at || new Date().toISOString(),
      metadata,
      persisted,
    });

    // Toast summary picks the title when the backend provides one,
    // and a more specific severity for known notification types.
    const summary = notif.title
      || (notif.type === 'FORM_SUBMISSION_RECEIVED' ? 'New Application'
        : notif.type === 'FORM_SUBMISSION_ASSIGNED' ? 'Application Assigned'
          : 'New Message');

    const severity = notif.type === 'FORM_SUBMISSION_RECEIVED' ? 'success'
      : notif.type === 'FORM_SUBMISSION_ASSIGNED' ? 'info'
        : 'info';

    toast.add({
      severity,
      summary,
      detail: notif.message,
      life: 5000,
      group: 'br',
    });
  }
});
</script>

<style scoped>
.aside {
  transition: width 0.3s ease;
}
</style>
